import { ApiError } from "@allergy123/api-client";
import type { AnswerValue, EncounterDetail, EncounterTiming, FormDefinition, FormSection, Observation, PatientDetail, TreatmentOrder } from "@allergy123/contracts/clinical";
import { Button, Card, Select, Status } from "@allergy123/ui";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router";
import { useClinical, useLoad } from "../api";
import { QuestionField, SectionForm, visible, type Answers } from "../encounter/QuestionField";
import { ReactionsGrid, type Readings } from "../encounter/ReactionsGrid";
import { ResultsView, suggestedIds, type CurrentTreatment } from "../encounter/ResultsView";
import { SignatureCard } from "../encounter/SignatureCard";
import { TimerStep } from "../encounter/TimerStep";
import { attestationLabel } from "../lib/attestations";
import { errorText, isConflict, isNotFound, missingItems } from "../lib/errors";
import { formatDateTime, fullName, isTestKind, kindLabel, statusLabel } from "../lib/format";
import { useSession } from "../session";

/** What the server asks the submitter to confirm (D-014, Q-09). */
const CONFIRMATIONS: Record<string, string> = {
  negative_test: "Every allergen read 0 mm. Record this as a negative test?",
  histamine_absent: "The histamine control did not react while other wells did. The test stays valid; continue?",
};

interface Draft {
  answers: Answers;
  readings: Readings;
  timing: EncounterTiming;
}
interface Step {
  key: string;
  label: string;
  section?: FormSection;
}


const fromDetail = (d: EncounterDetail): Draft => {
  const source = d.draft ?? d.revisions[d.revisions.length - 1];
  return {
    answers: Object.fromEntries((source?.answers ?? []).map((a) => [a.questionKey, a.value])),
    readings: Object.fromEntries((source?.observations ?? []).map((o) => [o.antigenId, o.value])),
    timing: source?.timing ?? {},
  };
};

function stepsFor(form: FormDefinition, enabled: (kind: string) => boolean): Step[] {
  if (isTestKind(form.kind)) {
    return [
      { key: "patient", label: "Patient info", section: form.sections.find((s) => s.key === "patient_info") },
      // The agreement step exists only when the practice collects that signature (D-014).
      ...(enabled("financial_agreement") ? [{ key: "agreement", label: "Agreement" }] : []),
      { key: "timer", label: "Timer" },
      { key: "questionnaire", label: "Questionnaire", section: form.sections.find((s) => s.key === "questionnaire") },
      { key: "reactions", label: "Reactions" },
      { key: "results", label: "Results" },
      { key: "order", label: "Order" },
    ];
  }
  const signatures = form.requiredAttestations.always.filter(enabled);
  return [...[...form.sections].sort((a, b) => a.order - b.order).map((s) => ({ key: `section:${s.key}`, label: s.title, section: s })), ...(signatures.length ? [{ key: "sign", label: "Signatures" }] : []), { key: "review", label: "Submit" }];
}

/**
 * One encounter from start to signed (HANDOFF.md Task 3.5): the stepper saves the draft on every step change under
 * the encounter's version; the server validates, runs the ruleset at submit, and applies the signing rule. The
 * browser never computes a result and never decides what is allowed.
 */
export function EncounterPage() {
  const { id = "" } = useParams();
  const api = useClinical();
  const s = useSession();
  const navigate = useNavigate();
  const [detail, setDetail] = useState<EncounterDetail | null>(null);
  const [loadError, setLoadError] = useState<unknown>(null);
  const [draft, setDraft] = useState<Draft>({ answers: {}, readings: {}, timing: {} });
  const [dirty, setDirty] = useState(false);
  const [step, setStep] = useState<number | null>(null);
  const [notice, setNotice] = useState<{ kind: "info" | "error" | "warning"; text: string; items?: string[] } | null>(null);
  const [busy, setBusy] = useState(false);
  const [selection, setSelection] = useState<string[]>([]);
  const [treatment, setTreatment] = useState<CurrentTreatment | null | undefined>(undefined);
  const [confirm, setConfirm] = useState<{ conditions: string[]; mode: "submit" | "correction" } | null>(null);
  const [correcting, setCorrecting] = useState(false);
  const [correctionReason, setCorrectionReason] = useState("");
  const timezone = s.location?.timezone ?? "America/Denver";

  const load = useCallback(async () => {
    try {
      const d = await api.encounters.get(id);
      setDetail(d);
      setDraft(fromDetail(d));
      setDirty(false);
      if (d.result) setSelection((sel) => (sel.length ? sel : suggestedIds(d.result!)));
    } catch (err) {
      setLoadError(err);
    }
  }, [api, id]);
  useEffect(() => {
    void load();
  }, [load]);

  const kind = detail?.encounter.kind;
  const form = useLoad((a) => (kind ? a.reference.form(kind) : Promise.resolve(null)), [kind]);
  const patient = useLoad((a) => (detail ? a.patients.get(detail.encounter.patientId) : Promise.resolve(null)), [detail?.encounter.patientId]);
  const panel = useLoad((a) => (detail && isTestKind(detail.encounter.kind) ? a.reference.locationPanel(detail.encounter.locationId) : Promise.resolve(null)), [detail?.encounter.locationId, kind]);
  const needsVials = form.data?.sections.some((sec) => sec.questions.some((q) => q.valueType === "vial_reference")) ?? false;
  const vials = useLoad((a) => (needsVials ? a.reference.vialProtocols() : Promise.resolve(null)), [needsVials]);

  const steps = useMemo(() => (form.data ? stepsFor(form.data, s.signatureEnabled) : []), [form.data, s]);

  // The patient's current treatment, for "Continue current" on the results step: the newest submitted order on an earlier test (inventory.md 4.6).
  useEffect(() => {
    if (!detail || treatment !== undefined || !isTestKind(detail.encounter.kind) || detail.encounter.status === "draft") return;
    let live = true;
    (async () => {
      const page = await api.patients.listEncounters(detail.encounter.patientId, { kind: ["test", "retest", "continuation"], status: ["signed", "corrected"], limit: 10 });
      for (const e of page.items) {
        if (e.encounterId === detail.encounter.encounterId) continue;
        const prior = await api.encounters.get(e.encounterId);
        const order = prior.orders.find((o) => o.status === "submitted");
        if (order) return { encounterId: e.encounterId, treatmentYear: order.treatmentYear, components: order.components.map((c) => ({ antigenId: c.antigenId, name: c.name })) };
      }
      return null;
    })()
      .then((found) => live && setTreatment(found))
      .catch(() => live && setTreatment(null));
    return () => {
      live = false;
    };
  }, [api, detail, treatment]);
  const inDraft = detail?.encounter.status === "draft";
  useEffect(() => {
    if (step !== null || !detail || steps.length === 0) return;
    const start = inDraft ? 0 : isTestKind(detail.encounter.kind) ? steps.findIndex((x) => x.key === (detail.orders.some((o) => o.status !== "cancelled" && o.status !== "superseded") ? "order" : "results")) : steps.length - 1;
    setStep(Math.max(0, start));
  }, [step, detail, steps, inDraft]);

  const positions = panel.data?.positions ?? [];
  const currentKey = step !== null ? steps[step]?.key : undefined;

  // Every well carries a value (D-014, Q-09): wells with no reading start at 0 mm when the reactions step opens.
  useEffect(() => {
    if (!inDraft || currentKey !== "reactions" || positions.length === 0) return;
    const blank = positions.filter((p) => draft.readings[p.antigenId] === null || draft.readings[p.antigenId] === undefined);
    if (blank.length === 0) return;
    setDraft((d) => ({ ...d, readings: { ...d.readings, ...Object.fromEntries(blank.map((p) => [p.antigenId, 0])) } }));
    setDirty(true);
  }, [inDraft, currentKey, positions, draft.readings]);
  const observations = useCallback((): Observation[] => (positions.length ? positions.map((p) => ({ antigenId: p.antigenId, position: p.position, value: draft.readings[p.antigenId] ?? null })) : Object.entries(draft.readings).map(([antigenId, value]) => ({ antigenId, value }))), [positions, draft.readings]);

  /** Saves the draft under the current version; on a conflict reloads and says so (Q-22). */
  async function persist(next: Partial<Draft> = {}): Promise<EncounterDetail | null> {
    if (!detail) return null;
    const d = { ...draft, ...next };
    setDraft(d);
    setBusy(true);
    try {
      const res = await api.encounters.saveDraft(id, { version: detail.encounter.version, answers: Object.entries(d.answers).filter(([, v]) => v !== undefined).map(([questionKey, value]) => ({ questionKey, value })), observations: positions.length ? positions.map((p) => ({ antigenId: p.antigenId, position: p.position, value: d.readings[p.antigenId] ?? null })) : observations(), timing: d.timing });
      setDetail(res);
      setDirty(false);
      return res;
    } catch (err) {
      if (isConflict(err)) {
        await load();
        setNotice({ kind: "warning", text: "Someone else saved this encounter first. The latest draft is shown; check it and continue." });
      } else setNotice({ kind: "error", text: `Couldn't save the draft: ${errorText(err)}` });
      return null;
    } finally {
      setBusy(false);
    }
  }

  async function goTo(index: number) {
    setNotice(null);
    if (dirty && inDraft) {
      const saved = await persist();
      if (!saved) return;
    }
    setStep(index);
  }

  const setAnswer = (key: string, value: AnswerValue | null) => {
    setDraft((d) => ({ ...d, answers: { ...d.answers, [key]: value } }));
    setDirty(true);
  };
  const setReading = (antigenId: string, value: number | null) => {
    setDraft((d) => ({ ...d, readings: { ...d.readings, [antigenId]: value } }));
    setDirty(true);
  };

  async function submit(acknowledge: string[] = []) {
    if (!detail) return;
    setNotice(null);
    setConfirm(null);
    const timing = isTestKind(detail.encounter.kind) ? { ...draft.timing, readAt: draft.timing.readAt ?? new Date().toISOString() } : draft.timing;
    const saved = await persist({ timing });
    if (!saved) return;
    setBusy(true);
    try {
      const res = await api.encounters.submit(id, { version: saved.encounter.version, acknowledge: acknowledge as ("negative_test" | "histamine_absent")[] });
      setDetail(res);
      if (res.result) setSelection(suggestedIds(res.result));
      setStep(steps.findIndex((x) => x.key === (isTestKind(res.encounter.kind) ? "results" : "review")));
    } catch (err) {
      if (err instanceof ApiError && err.code === "confirm_required") setConfirm({ conditions: (err.details.conditions as string[]) ?? [], mode: "submit" });
      else {
        const items = missingItems(err);
        setNotice(items.length ? { kind: "warning", text: "Not complete yet:", items } : { kind: "error", text: errorText(err) });
      }
    } finally {
      setBusy(false);
    }
  }

  /** A correction creates a new revision from the edited answers and readings; the order, if any, is untouched (Q-75). */
  async function saveCorrection(acknowledge: string[] = []) {
    if (!detail || !correctionReason.trim()) return;
    setBusy(true);
    setNotice(null);
    setConfirm(null);
    try {
      const res = await api.encounters.correct(id, { reason: correctionReason.trim(), answers: Object.entries(draft.answers).filter(([, v]) => v !== undefined).map(([questionKey, value]) => ({ questionKey, value })), observations: observations(), timing: draft.timing, acknowledge: acknowledge as ("negative_test" | "histamine_absent")[] });
      setDetail(res);
      setDraft(fromDetail(res));
      setDirty(false);
      setCorrecting(false);
      setCorrectionReason("");
      if (res.result) setSelection(suggestedIds(res.result));
      setStep(steps.findIndex((x) => x.key === (isTestKind(res.encounter.kind) ? "results" : "review")));
      setNotice({ kind: "info", text: `Correction saved as revision ${res.revisions.length}.` });
    } catch (err) {
      if (err instanceof ApiError && err.code === "confirm_required") setConfirm({ conditions: (err.details.conditions as string[]) ?? [], mode: "correction" });
      else {
        const items = missingItems(err);
        setNotice(items.length ? { kind: "warning", text: "Not complete yet:", items } : { kind: "error", text: errorText(err) });
      }
    } finally {
      setBusy(false);
    }
  }

  async function act<T>(fn: () => Promise<T>, after?: (result: T) => void) {
    setBusy(true);
    setNotice(null);
    try {
      const result = await fn();
      after?.(result);
      await load();
    } catch (err) {
      const items = missingItems(err);
      setNotice(items.length ? { kind: "warning", text: "Not complete yet:", items } : { kind: "error", text: errorText(err) });
    } finally {
      setBusy(false);
    }
  }

  if (loadError) {
    return (
      <Card tone="tinted">
        <h1 className="mb-2 text-h2 font-semibold text-anchor">{isNotFound(loadError) ? "Encounter not found" : "Couldn't load the encounter"}</h1>
        <p className="mb-4 text-ink-secondary">{errorText(loadError)}</p>
        <Button variant="secondary" surface="card" href="/">
          Back to today
        </Button>
      </Card>
    );
  }
  if (!detail || !form.data || step === null) return <p className="text-ink-secondary">Loading…</p>;
  if (form.state === "error") return <Status kind="error" label={`Couldn't load the form: ${errorText(form.error)}`} />;

  const e = detail.encounter;
  const current = steps[step]!;
  const currentRevision = detail.revisions[detail.revisions.length - 1];
  const attested = (kindOf: string, subjectId = e.encounterId) => detail.attestations.find((a) => a.kind === kindOf && a.subjectId === subjectId);
  const activeOrder = detail.orders.find((o) => o.status === "draft" || o.status === "signed" || o.status === "submitted");
  const stepEnabled = (x: Step) => (x.key === "results" || x.key === "order" ? !inDraft : true);
  const canWrite = s.can("clinical:write");
  const canCorrect = !inDraft && ["submitted", "signed", "corrected"].includes(e.status) && s.can("clinical:order");
  const editable = canWrite && (inDraft || correcting);
  const correctionBar = canCorrect && (current.section || current.key === "reactions") && (
    <div className="mb-4 flex flex-wrap items-end gap-3 rounded-md border border-rule-subtle bg-card-tinted p-3">
      {!correcting ? (
        <>
          <p className="text-small text-ink-secondary">Revision {detail.revisions.length} is fixed. A correction records a new revision with its reason; earlier revisions stay visible.</p>
          <Button variant="secondary" surface="card-tinted" disabled={busy} onClick={() => setCorrecting(true)}>
            Make a correction
          </Button>
        </>
      ) : (
        <>
          <label className="flex min-w-[20rem] flex-1 flex-col gap-1 text-small font-medium text-ink">
            Reason for the correction
            <input className="min-h-10 rounded-sm border border-rule-strong bg-card px-3 text-body font-normal" value={correctionReason} onChange={(ev) => setCorrectionReason(ev.target.value)} maxLength={1000} required />
          </label>
          <Button disabled={busy || !correctionReason.trim()} onClick={() => void saveCorrection()}>
            Save correction
          </Button>
          <Button variant="secondary" surface="card-tinted" disabled={busy} onClick={() => { setCorrecting(false); setDraft(fromDetail(detail)); setDirty(false); }}>
            Discard
          </Button>
        </>
      )}
    </div>
  );

  return (
    <>
      <div className="mb-6 flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="mb-1 text-small text-ink-secondary">
            <Link to="/">Today</Link> · {patient.data ? <Link to={`/patients/${e.patientId}`}>{fullName(patient.data)}</Link> : "Patient"}
          </p>
          <h1 className="mb-1 text-h1 font-semibold text-anchor">
            {kindLabel(e.kind)}
          </h1>
          <p className="text-ink-secondary">
            {statusLabel(e.status)} · started {formatDateTime(e.startedAt, timezone)}
            {patient.data && ` · born ${patient.data.dateOfBirth}`}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          {inDraft && canWrite && (
            <Button variant="secondary" disabled={busy || !dirty} onClick={() => void persist()}>
              Save draft
            </Button>
          )}
          {inDraft && canWrite && <CancelEncounter busy={busy} onCancel={(reason) => act(() => api.encounters.cancel(id, { version: e.version, reason }), () => navigate(`/patients/${e.patientId}`))} />}
        </div>
      </div>

      <nav aria-label="Steps" className="mb-6 flex flex-wrap gap-2 border-b border-rule-subtle pb-4">
        {steps.map((x, i) => (
          <button key={x.key} type="button" aria-current={i === step ? "step" : undefined} disabled={!stepEnabled(x) || busy} onClick={() => void goTo(i)} className={`rounded-sm px-3 py-2 text-body disabled:text-ink-secondary ${i === step ? "bg-selection font-semibold text-anchor" : "text-ink hover:bg-card-tinted"}`}>
            {i + 1}. {x.label}
          </button>
        ))}
      </nav>

      {confirm && (
        <Card tone="tinted" className="mb-6" role="alertdialog" aria-labelledby="confirm-title">
          <h2 id="confirm-title" className="mb-2 text-h3 font-semibold text-anchor">
            Please confirm before {confirm.mode === "submit" ? "submitting" : "saving the correction"}
          </h2>
          <ul className="mb-4 list-disc pl-6">
            {confirm.conditions.map((c) => (
              <li key={c}>{CONFIRMATIONS[c] ?? c}</li>
            ))}
          </ul>
          <div className="flex flex-wrap gap-2">
            <Button disabled={busy} onClick={() => void (confirm.mode === "submit" ? submit(confirm.conditions) : saveCorrection(confirm.conditions))}>
              Confirm and {confirm.mode === "submit" ? "submit" : "save"}
            </Button>
            <Button variant="secondary" surface="card-tinted" onClick={() => setConfirm(null)}>
              Go back to the readings
            </Button>
          </div>
        </Card>
      )}
      {notice && (
        <div className="mb-6">
          <Status kind={notice.kind} label={notice.text} />
          {notice.items && (
            <ul className="mt-2 list-disc pl-6 text-body">
              {notice.items.map((m) => (
                <li key={m}>{m}</li>
              ))}
            </ul>
          )}
        </div>
      )}

      <Card as="section" aria-labelledby="step-title">
        <h2 id="step-title" className="mb-4 text-h2 font-semibold text-anchor">
          {current.label}
        </h2>

        {current.section && (
          <>
            {current.key === "patient" && patient.data && <PatientSummaryBlock p={patient.data} />}
            {correctionBar}
            <SectionForm section={current.section} answers={draft.answers} onAnswer={setAnswer} vials={vials.data ?? undefined} disabled={!editable} />
          </>
        )}

        {current.key === "agreement" && (
          <div className="flex flex-col gap-4">
            {patient.data && <BenefitsBlock p={patient.data} kind="test" />}
            <SignatureCard kind="financial_agreement" subjectType="encounter" subjectId={e.encounterId} existing={attested("financial_agreement")} timezone={timezone} disabled={!canWrite} onSigned={() => void load()} />
          </div>
        )}

        {current.key === "timer" && <TimerStep timing={draft.timing} histamineMinutes={panel.data?.timers.histamineReadMinutes ?? 10} fullMinutes={panel.data?.timers.fullReadMinutes ?? 15} timezone={timezone} disabled={!inDraft || !canWrite || busy} onStart={() => persist({ timing: { ...draft.timing, appliedAt: new Date().toISOString() } }).then(() => undefined)} />}

        {current.key === "reactions" &&
          (panel.state === "error" ? (
            <Status kind="error" label={`Couldn't load the tray panel: ${errorText(panel.error)}`} />
          ) : positions.length ? (
            <div className="flex flex-col gap-6">
              {panel.data && e.trayPanelVersionId && panel.data.trayPanel.versionId !== e.trayPanelVersionId && <Status kind="info" label="This encounter is pinned to an earlier panel version than the location's current one; the pinned version is used for the result." />}
              {correctionBar}
              <ReactionsGrid key={`${detail.revisions.length}-${correcting}-${positions.every((p) => draft.readings[p.antigenId] !== null && draft.readings[p.antigenId] !== undefined)}`} positions={positions} readings={draft.readings} onChange={setReading} disabled={!editable} />
              {inDraft && canWrite && (
                <div className="flex flex-wrap items-center gap-3">
                  <Button onClick={() => void submit()} disabled={busy}>
                    Submit test
                  </Button>
                  <span className="text-small text-ink-secondary">Submitting records the read time, runs the ruleset and fixes this revision; corrections create a new one.</span>
                </div>
              )}
            </div>
          ) : (
            <p className="text-ink-secondary">Loading the tray panel…</p>
          ))}

        {current.key === "results" && detail.revisions.length > 1 && <RevisionsPanel detail={detail} timezone={timezone} />}
        {current.key === "results" && (
          <ResultsStep detail={detail} form={form.data} positions={positions} selection={selection} onSelection={setSelection} busy={busy} canOrder={s.can("clinical:order")} canWrite={canWrite} timezone={timezone} current={treatment ?? null} onSigned={() => void load()} onOrder={() => setStep(steps.findIndex((x) => x.key === "order"))} onDecline={(reason) => act(() => api.encounters.decline(id, { revisionId: currentRevision!.revisionId, reason, selection }))} />
        )}

        {current.key === "order" && (
          <OrderStep detail={detail} form={form.data} positions={positions} selection={selection} order={activeOrder} busy={busy} canOrder={s.can("clinical:order")} canWrite={canWrite} timezone={timezone} onSigned={() => void load()} onCreate={(treatmentYear, diagnoses) => act(() => api.encounters.createOrder(id, { revisionId: currentRevision!.revisionId, components: selection.map((antigenId) => ({ antigenId })), treatmentYear, diagnoses }))} onSubmit={(order) => act(() => api.orders.submit(order.orderId, { version: order.version }))} onCancel={(order, reason) => act(() => api.orders.cancel(order.orderId, { version: order.version, reason }))} />
        )}

        {current.key === "sign" && (
          <div className="flex flex-col gap-4">
            {form.data.requiredAttestations.always.filter(s.signatureEnabled).map((k) => (
              <SignatureCard key={k} kind={k} subjectType="encounter" subjectId={e.encounterId} existing={attested(k)} timezone={timezone} disabled={!canWrite} onSigned={() => void load()} />
            ))}
          </div>
        )}

        {current.key === "review" && (
          <ReviewStep detail={detail} busy={busy} canWrite={canWrite} timezone={timezone} onSubmit={() => void submit()} />
        )}

        {current.key !== "review" && current.key !== "order" && (
          <div className="mt-8 flex flex-wrap justify-between gap-2 border-t border-rule-subtle pt-4">
            <Button variant="secondary" surface="card" disabled={step === 0 || busy} onClick={() => void goTo(step - 1)}>
              Back
            </Button>
            {step < steps.length - 1 && stepEnabled(steps[step + 1]!) && (
              <Button disabled={busy} onClick={() => void goTo(step + 1)}>
                Next: {steps[step + 1]!.label}
              </Button>
            )}
          </div>
        )}
      </Card>
    </>
  );
}

/** The service report's state, with a download through a short-lived link once a version is ready (ADR 0007). */
function ReportStatus({ detail }: { detail: EncounterDetail }) {
  const api = useClinical();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const reports = detail.documents.filter((d) => d.kind === "service_report");
  if (reports.length === 0) return null;
  async function open(documentId: string, versionId: string) {
    setBusy(true);
    setError(null);
    try {
      const link = await api.documents.access(documentId, versionId, { disposition: "inline" });
      window.open(link.url, "_blank", "noopener");
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  }
  return (
    <div className="flex flex-col gap-2">
      {reports.map((d) => {
        const ready = d.currentVersionId ? d.versions.find((v) => v.versionId === d.currentVersionId) : undefined;
        const latest = d.versions[0];
        return (
          <div key={d.documentId} className="flex flex-wrap items-center gap-3">
            <span className="text-small text-ink-secondary">Service report: {latest ? statusLabel(latest.status) : "none"}{latest?.status === "failed" && latest.failureReason ? ` (${latest.failureReason})` : ""}</span>
            {ready && (
              <Button variant="secondary" disabled={busy} onClick={() => void open(d.documentId, ready.versionId)}>
                Open report (PDF)
              </Button>
            )}
          </div>
        );
      })}
      {error && <Status kind="error" label={error} />}
    </div>
  );
}

/** Every revision of the encounter, oldest first; the current one carries the full result, earlier ones their summary. */
function RevisionsPanel({ detail, timezone }: { detail: EncounterDetail; timezone: string }) {
  return (
    <Card tone="tinted" dense className="mb-6">
      <h3 className="mb-2 text-h3 font-semibold text-anchor">Revisions</h3>
      <ol className="flex flex-col gap-1 text-body">
        {detail.revisions.map((r, i) => (
          <li key={r.revisionId} className={i === detail.revisions.length - 1 ? "font-medium" : undefined}>
            Revision {r.revision} · {formatDateTime(r.submittedAt, timezone)}
            {r.reason && ` · ${r.reason}`}
            {r.result && ` · ${r.result.suggestedCount} suggested, ${r.result.otherCount} other`}
            {i === detail.revisions.length - 1 && " (current)"}
          </li>
        ))}
      </ol>
    </Card>
  );
}

function PatientSummaryBlock({ p }: { p: PatientDetail }) {
  return (
    <dl className="mb-6 grid grid-cols-[auto_1fr] gap-x-4 gap-y-1 rounded-md bg-card-tinted p-4 text-body sm:grid-cols-[auto_1fr_auto_1fr]">
      <dt className="text-ink-secondary">Patient</dt>
      <dd>{fullName(p)}</dd>
      <dt className="text-ink-secondary">Born</dt>
      <dd>{p.dateOfBirth}</dd>
      <dt className="text-ink-secondary">Insurance</dt>
      <dd>{p.insurance?.carrier ?? "Not recorded"}</dd>
      <dt className="text-ink-secondary">Member</dt>
      <dd>{p.insurance?.memberId ?? "—"}</dd>
    </dl>
  );
}

const dollars = (cents: number | null) => (cents === null ? "—" : `$${(cents / 100).toFixed(2)}`);

function BenefitsBlock({ p, kind }: { p: PatientDetail; kind: "test" | "immunotherapy" }) {
  const b = p.benefits.find((x) => x.benefitKind === kind);
  if (!b) return <p className="text-ink-secondary">No {kind} benefit figures are recorded for this patient; the agreement is signed without them.</p>;
  return (
    <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1 rounded-md bg-card-tinted p-4 text-body sm:grid-cols-[auto_1fr_auto_1fr]">
      <dt className="text-ink-secondary">Deductible</dt>
      <dd>
        {dollars(b.deductibleCents)} ({dollars(b.deductibleMetCents)} met)
      </dd>
      <dt className="text-ink-secondary">Out of pocket</dt>
      <dd>
        {dollars(b.outOfPocketMaxCents)} ({dollars(b.outOfPocketMetCents)} met)
      </dd>
      <dt className="text-ink-secondary">Coinsurance</dt>
      <dd>{b.coinsurancePercent === null ? "—" : `${b.coinsurancePercent}%`}</dd>
      <dt className="text-ink-secondary">Copay</dt>
      <dd>{dollars(b.copayCents)}</dd>
      {b.authRequired && (
        <>
          <dt className="text-ink-secondary">Authorization</dt>
          <dd>Required{b.authAction ? `: ${b.authAction}` : ""}</dd>
        </>
      )}
    </dl>
  );
}

function CancelEncounter({ busy, onCancel }: { busy: boolean; onCancel: (reason: string | null) => void }) {
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState("");
  if (!open)
    return (
      <Button variant="secondary" disabled={busy} onClick={() => setOpen(true)}>
        Cancel encounter
      </Button>
    );
  return (
    <form
      className="flex flex-wrap items-end gap-2"
      onSubmit={(ev) => {
        ev.preventDefault();
        onCancel(reason.trim() || null);
      }}
    >
      <label className="flex flex-col gap-1 text-small font-medium text-ink">
        Reason
        <input className="min-h-10 rounded-sm border border-rule-strong bg-card px-3 text-body font-normal" value={reason} onChange={(ev) => setReason(ev.target.value)} maxLength={500} />
      </label>
      <Button type="submit" variant="secondary" disabled={busy}>
        Confirm cancel
      </Button>
      <Button type="button" variant="secondary" onClick={() => setOpen(false)}>
        Keep
      </Button>
    </form>
  );
}

function ResultsStep({ detail, form, positions, selection, onSelection, busy, canOrder, canWrite, timezone, current, onSigned, onOrder, onDecline }: { detail: EncounterDetail; form: FormDefinition; positions: Parameters<typeof ResultsView>[0]["positions"]; selection: string[]; onSelection: (ids: string[]) => void; busy: boolean; canOrder: boolean; canWrite: boolean; timezone: string; current: CurrentTreatment | null; onSigned: () => void; onOrder: () => void; onDecline: (reason: string | null) => void }) {
  const [declining, setDeclining] = useState(false);
  const [reason, setReason] = useState("");
  const e = detail.encounter;
  const revision = detail.revisions[detail.revisions.length - 1];
  if (!detail.result || !revision) return <p className="text-ink-secondary">No result yet.</p>;
  const s = useSession();
  const present = new Set(detail.attestations.filter((a) => a.subjectId === e.encounterId).map((a) => a.kind));
  const forDecline = [...form.requiredAttestations.always, ...form.requiredAttestations.withDecline].filter((k, i, arr) => arr.indexOf(k) === i).filter(s.signatureEnabled);
  const missingForDecline = forDecline.filter((k) => !present.has(k));
  const reviewed = detail.attestations.find((a) => a.kind === "results_reviewed" && a.subjectId === e.encounterId);
  const activeOrder = detail.orders.find((o) => o.status === "draft" || o.status === "signed" || o.status === "submitted");
  const settled = !!detail.decline || !!activeOrder;
  return (
    <div className="flex flex-col gap-6">
      {detail.decline && <Status kind="info" label={`Treatment declined ${formatDateTime(detail.decline.declinedAt, timezone)}${detail.decline.reason ? `: ${detail.decline.reason}` : "."}`} />}
      <ResultsView result={detail.result} positions={positions} observations={revision.observations} selection={selection} onSelection={onSelection} readOnly={settled || !canOrder} chosen={detail.result.chosen} current={current} />
      {!settled && s.signatureEnabled("results_reviewed") && <SignatureCard kind="results_reviewed" subjectType="encounter" subjectId={e.encounterId} existing={reviewed} timezone={timezone} disabled={!canWrite} onSigned={onSigned} />}
      {!settled && canOrder && (
        <div className="flex flex-wrap items-center gap-3 border-t border-rule-subtle pt-4">
          <Button disabled={busy || selection.length === 0} onClick={onOrder}>
            Order therapy
          </Button>
          {!declining ? (
            <Button variant="secondary" disabled={busy} onClick={() => setDeclining(true)}>
              Do not order therapy
            </Button>
          ) : (
            <form
              className="flex flex-wrap items-end gap-2"
              onSubmit={(ev) => {
                ev.preventDefault();
                onDecline(reason.trim() || null);
              }}
            >
              <label className="flex flex-col gap-1 text-small font-medium text-ink">
                Reason (optional)
                <input className="min-h-10 rounded-sm border border-rule-strong bg-card px-3 text-body font-normal" value={reason} onChange={(ev) => setReason(ev.target.value)} maxLength={1000} />
              </label>
              <Button type="submit" variant="secondary" disabled={busy || missingForDecline.length > 0}>
                Confirm: test only, no immunotherapy
              </Button>
              <Button type="button" variant="secondary" onClick={() => setDeclining(false)}>
                Back
              </Button>
              {missingForDecline.length > 0 && <Status kind="info" label={`Declining needs: ${missingForDecline.map(attestationLabel).join(", ")}.`} />}
            </form>
          )}
        </div>
      )}
      {!canOrder && !settled && <Status kind="info" label="Ordering or declining treatment needs a provider with ordering permission." />}
      {activeOrder && <Status kind="info" label={`An order exists (${statusLabel(activeOrder.status)}). Continue on the Order step.`} />}
    </div>
  );
}

function OrderStep({ detail, form, positions, selection, order, busy, canOrder, canWrite, timezone, onSigned, onCreate, onSubmit, onCancel }: { detail: EncounterDetail; form: FormDefinition; positions: Parameters<typeof ResultsView>[0]["positions"]; selection: string[]; order?: TreatmentOrder; busy: boolean; canOrder: boolean; canWrite: boolean; timezone: string; onSigned: () => void; onCreate: (treatmentYear: number, diagnoses: Array<{ codeSystem: "ICD-10-CM"; code: string }>) => void; onSubmit: (order: TreatmentOrder) => void; onCancel: (order: TreatmentOrder, reason: string) => void }) {
  const s = useSession();
  const [year, setYear] = useState(1);
  const [cancelReason, setCancelReason] = useState("");
  const e = detail.encounter;
  const revision = detail.revisions[detail.revisions.length - 1];
  const names = new Map(positions.map((p) => [p.antigenId, p.displayName]));
  const answers: Answers = Object.fromEntries((revision?.answers ?? []).map((a) => [a.questionKey, a.value]));
  const diagnoses = form.sections.flatMap((sec) => sec.questions).filter((q) => q.codeSystem === "ICD-10-CM" && q.code && answers[q.key] === true && visible(q, answers)).map((q) => ({ codeSystem: "ICD-10-CM" as const, code: q.code! }));
  const attested = (k: string, subjectId: string) => detail.attestations.find((a) => a.kind === k && a.subjectId === subjectId);

  if (detail.decline) return <Status kind="info" label="Treatment was declined for this test; no order can be placed." />;
  if (!order) {
    if (!canOrder) return <Status kind="info" label="Creating an order needs a provider with ordering permission." />;
    return (
      <div className="flex flex-col gap-5">
        <div>
          <h3 className="mb-2 text-h3 font-semibold text-anchor">Components</h3>
          {selection.length === 0 ? <p className="text-ink-secondary">Pick antigens on the Results step first.</p> : <ol className="list-decimal pl-6">{selection.map((id) => <li key={id}>{names.get(id) ?? id}</li>)}</ol>}
        </div>
        <Select label="Treatment year" options={[1, 2, 3, 4, 5, 6].map((y) => ({ value: String(y), label: `Year ${y}` }))} value={String(year)} onChange={(ev) => setYear(Number(ev.target.value))} />
        <div>
          <h3 className="mb-2 text-h3 font-semibold text-anchor">Diagnoses</h3>
          <p className="text-ink-secondary">{diagnoses.length ? `Answered: ${diagnoses.map((d) => d.code).join(", ")}. ` : "None answered. "}The server adds the codes derived from the ordered antigens.</p>
        </div>
        <div>
          <Button disabled={busy || selection.length === 0} onClick={() => onCreate(year, diagnoses)}>
            Create order
          </Button>
        </div>
      </div>
    );
  }

  const benefitKinds = (["benefit_check_provider", "benefit_check_patient"] as const).filter(s.signatureEnabled);
  const benefitSigned = benefitKinds.every((k) => attested(k, order.orderId));
  const canSubmit = (order.status === "signed" || benefitSigned) && detail.requiredAttestations.length === 0;
  return (
    <div className="flex flex-col gap-5">
      <Card tone="tinted" dense>
        <p className="text-small text-ink-secondary">Order · {statusLabel(order.status)} · treatment year {order.treatmentYear}</p>
        <ol className="list-decimal pl-6">
          {order.components.map((c) => (
            <li key={c.antigenId}>{c.name}</li>
          ))}
        </ol>
        <p className="mt-2 text-small text-ink-secondary">Diagnoses: {order.diagnoses.map((d) => `${d.code}${d.source === "derived_from_antigen" ? " (derived)" : ""}`).join(", ") || "none"}</p>
      </Card>
      {order.status === "submitted" ? (
        <>
          <Status kind="success" label={`Order submitted ${order.submittedAt ? formatDateTime(order.submittedAt, timezone) : ""}. ${e.status === "signed" ? "The encounter is signed and the service report is queued." : `Encounter ${statusLabel(e.status)}.`}`} />
          <ReportStatus detail={detail} />
          <div>
            <Button variant="secondary" href={`/patients/${e.patientId}`}>
              Back to the patient
            </Button>
          </div>
        </>
      ) : (
        <>
          {benefitKinds.length > 0 && <h3 className="text-h3 font-semibold text-anchor">Immunotherapy benefit check</h3>}
          {benefitKinds.map((k) => (
            <SignatureCard key={k} kind={k} subjectType="treatment_order" subjectId={order.orderId} existing={attested(k, order.orderId)} timezone={timezone} disabled={!canWrite} onSigned={onSigned} />
          ))}
          {form.requiredAttestations.withOrder.filter((k) => k !== "benefit_check_provider" && k !== "benefit_check_patient" && k !== "results_reviewed" && s.signatureEnabled(k)).map((k) => (
            <SignatureCard key={k} kind={k} subjectType="encounter" subjectId={e.encounterId} existing={attested(k, e.encounterId)} timezone={timezone} disabled={!canWrite} onSigned={onSigned} />
          ))}
          {detail.requiredAttestations.length > 0 && <Status kind="info" label={`Still needed before the order can be submitted: ${detail.requiredAttestations.map(attestationLabel).join(", ")}.`} />}
          <div className="flex flex-wrap items-end gap-3 border-t border-rule-subtle pt-4">
            <Button disabled={busy || !canSubmit || !canOrder} onClick={() => onSubmit(order)}>
              Submit order
            </Button>
            {canOrder && (
              <form
                className="flex flex-wrap items-end gap-2"
                onSubmit={(ev) => {
                  ev.preventDefault();
                  if (cancelReason.trim()) onCancel(order, cancelReason.trim());
                }}
              >
                <label className="flex flex-col gap-1 text-small font-medium text-ink">
                  Cancel reason
                  <input className="min-h-10 rounded-sm border border-rule-strong bg-card px-3 text-body font-normal" value={cancelReason} onChange={(ev) => setCancelReason(ev.target.value)} maxLength={500} />
                </label>
                <Button type="submit" variant="secondary" disabled={busy || !cancelReason.trim()}>
                  Cancel order
                </Button>
              </form>
            )}
          </div>
        </>
      )}
    </div>
  );
}

function ReviewStep({ detail, busy, canWrite, timezone, onSubmit }: { detail: EncounterDetail; busy: boolean; canWrite: boolean; timezone: string; onSubmit: () => void }) {
  const e = detail.encounter;
  const revision = detail.revisions[detail.revisions.length - 1];
  if (e.status === "draft") {
    return (
      <div className="flex flex-col gap-4">
        <p className="max-w-[68ch] text-ink-secondary">Submitting fixes this visit as revision 1. Every required answer must be present and every signature on the previous step recorded; the server lists anything missing.</p>
        {detail.requiredAttestations.length > 0 && <Status kind="info" label={`Signatures still needed: ${detail.requiredAttestations.map(attestationLabel).join(", ")}.`} />}
        <div>
          <Button disabled={busy || !canWrite} onClick={onSubmit}>
            Submit {kindLabel(e.kind).toLowerCase()}
          </Button>
        </div>
      </div>
    );
  }
  return (
    <div className="flex flex-col gap-4">
      <Status kind="success" label={`${kindLabel(e.kind)} ${statusLabel(e.status).toLowerCase()}${e.signedAt ? ` ${formatDateTime(e.signedAt, timezone)}` : revision ? ` ${formatDateTime(revision.submittedAt, timezone)}` : ""}.`} />
      {revision?.computed.adjustmentScore !== null && revision?.computed.adjustmentScore !== undefined && <p>Adjustment score {revision.computed.adjustmentScore}: {revision.computed.adjustmentOutcome}</p>}
      {detail.requiredAttestations.length > 0 && <Status kind="info" label={`Signatures still needed to sign the visit: ${detail.requiredAttestations.map(attestationLabel).join(", ")}.`} />}
      <ReportStatus detail={detail} />
      <div>
        <Button variant="secondary" href={`/patients/${e.patientId}`}>
          Back to the patient
        </Button>
      </div>
    </div>
  );
}

export const _internal = { isTestKind, stepsFor, ApiError };
